import { AssetPack, AssetPacks, Component, loadFrom } from "./assets";
import * as compose from "./composition";
import * as chroma from "chroma-js";
import * as throttle from "./throttle";
import { Memento } from "./memento";
import Picker from "vanilla-picker";
import * as utils from "./utils";
import { PackSource, ServedPackSource, ZipPackSource } from "./pack_sources";
import * as JSZip from "jszip";

const mainCanvas = <HTMLCanvasElement>document.getElementById('av');
const assetsPicker = <HTMLSelectElement>document.getElementById("packPicker");
const zipPicker = <HTMLInputElement>document.getElementById("zipPicker");
const propsRoot = <HTMLDivElement>document.querySelector("#propsRoot");
const colorsRoot = <HTMLDivElement>document.querySelector("#colorsRoot");

var inputs: { [group: string]: HTMLSelectElement } = {};
var colors: { [color: string]: ColorBundle } = {};
var colorValues: { [color: string]: string } = {};
var updatingHash = false;
var currentPackSource: PackSource;
var currentAssets: AssetPack;
var packs: AssetPacks;
const updateThrottle = new throttle.WithWarmup(function () {
    return update(true);
}, 50);
const setHashThrottle = new throttle.WithResettingWarmup(function () {
    updatingHash = true;
    location.hash = createMemento(false).toHash();
}, 1000);

var waitingMemento: () => void = null;

function createMemento(short: boolean) {
    var props = readProps();
    const memento = new Memento();
    memento.assetPack = assetsPicker.value;
    memento.props = props;
    memento.colors = colorValues;
    if (short) {
        for (const colorId in colors) {
            if (!colors[colorId].inUse)
                delete memento.colors[colorId];
            else if (memento.colors[colorId] && memento.colors[colorId].match(/#\w\w\w\w\w\wff/))
                memento.colors[colorId] = memento.colors[colorId].slice(0, -2);
        }
    }
    return memento;
}

class ColorBundle {
    constructor(public picker: Picker, public code: HTMLSpanElement, public holder: HTMLDivElement) {
    }
    inUse: boolean
    updateActive() { this.inUse ? this.holder.classList.remove("inactive") : this.holder.classList.add("inactive"); }
}

async function asyncMain() {
    packs = await (await fetch("assets/assetpacks.json")).json();
    for (const packId in packs) {
        const pack = packs[packId];
        assetsPicker.appendChild(new Option(pack.displayName, packId))
    }
    assetsPicker.appendChild(new Option("Load from .zip", "zipfile"))

    window.onhashchange = async (e: HashChangeEvent) => {
        await applyMemento(Memento.fromHash(location.hash));
    };

    document.getElementById("btnDownload").onclick = () => { saveCanvas(); return false; };
    document.getElementById("btnRandomize").onclick = () => { randomize(); return false; };
    if (navigator.clipboard)
        document.getElementById("btnCopyLink").onclick = () => { copyLink(); return false; };
    else
        document.getElementById("btnCopyLink").remove();

    assetsPicker.onchange = async () => {
        if (assetsPicker.value == "zipfile") {
            zipPicker.style.display = "";
            return;
        } else {
            zipPicker.style.display = "none";
        }

        currentPackSource = new ServedPackSource(packs[assetsPicker.value].url);
        currentAssets = await loadFrom(currentPackSource);
        readAssets();
        await update(true);
    };

    zipPicker.onchange = async () => {
        if (zipPicker.files.length <= 0) return;

        const zip = await JSZip.loadAsync(zipPicker.files[0]);
        currentPackSource = new ZipPackSource(zip);
        currentAssets = await loadFrom(currentPackSource);
        readAssets();
        if (waitingMemento)
            waitingMemento();
        else
            await update(true);
    };

    if (location.hash != "")
        await applyMemento(Memento.fromHash(location.hash));

    if (assetsPicker.value != "zipfile") {
        currentPackSource = new ServedPackSource(packs[assetsPicker.value].url);
        currentAssets = await loadFrom(currentPackSource);
        readAssets();
        await update(false);
    }
}

// buttons

async function saveCanvas() { utils.downloadBlob(await utils.canvasToBlob(mainCanvas), "avatar"); }
async function copyLink() {
    const url = new URL(window.location.href);
    url.hash = createMemento(true).toHash();
    utils.copyTextToClipboard(url.toString());
}
function randomize() {

    const r = createMemento(false);

    for (const colorId in r.colors) {
        const randomColor = chroma.rgb(utils.getRandomInt(256), utils.getRandomInt(256), utils.getRandomInt(256));
        var rcolor = randomColor.brighten().desaturate();
        r.colors[colorId] = rcolor.hex();
    }
    for (const typeId in currentAssets.assets) {
        const keys = Object.keys(currentAssets.assets[typeId].items);
        r.props[typeId] = keys[utils.getRandomInt(keys.length)]
    }
    applyMemento(r);
}

function readAssets() {
    mainCanvas.width = currentAssets.render.canvasWidth;
    mainCanvas.height = currentAssets.render.canvasHeight;
    const props = readProps();

    utils.removeAllChildren(propsRoot);
    utils.removeAllChildren(colorsRoot);

    var oldColorValues = colorValues;
    colors = {};
    inputs = {};
    colorValues = {};

    for (const type in currentAssets.assets) { // generate parts selectors
        const t = currentAssets.assets[type];

        const holder = document.createElement("div");
        propsRoot.appendChild(holder);
        holder.className = "propHolder";

        const selectList = document.createElement("select");
        selectList.id = type + "Selector";
        selectList.className = "propHolder-prop";
        for (const assetId in t.items) {
            selectList.appendChild(new Option(t.items[assetId].displayName, assetId));
        }
        selectList.onchange = () => update(true);
        inputs[type] = selectList;

        if (props[type] && t.items[props[type]]) {
            inputs[type].value = props[type];
        }

        const label = document.createElement("label");
        holder.appendChild(label);
        label.innerText = t.displayName;
        label.className = "propHolder-descr";
        label.htmlFor = selectList.id;

        holder.appendChild(selectList);
    }

    for (const cname in currentAssets.colors) {
        const c = currentAssets.colors[cname];
        const holder = document.createElement("div");
        colorsRoot.appendChild(holder);
        holder.className = "pickerHolder";

        const label = document.createElement("label");
        label.className = "pickerHolder-descr";
        holder.appendChild(label);
        label.innerText = c.displayName + ": ";

        const code = document.createElement("span");
        code.id = cname + "Hex";
        code.className = "colorPreview pickerHolder-prop";
        holder.appendChild(code);
        const picker = new Picker({
            parent: holder,
            popup: "top",
            alpha: false,
            editorFormat: 'hex',
            onDone: function (color) {
                colorValues[cname] = color.hex;
                code.innerText = color.hex;
                code.style.backgroundColor = color.hex;
                update(true);
            },
        });

        let colorToSet = (oldColorValues[cname]) ? oldColorValues[cname] : c.default;

        colorValues[cname] = colorToSet;
        picker.setColor(colorToSet, true);
        code.innerText = colorToSet;
        code.style.backgroundColor = colorToSet;

        picker.onChange = function (color) {
            colorValues[cname] = color.hex;
            code.innerText = color.hex;
            code.style.backgroundColor = color.hex;
            updateThrottle.trigger();
        }

        colors[cname] = new ColorBundle(picker, code, holder);
    }

    document.getElementById("licenseRoot").innerHTML = utils.redneckMd(currentAssets.meta.license);
}

async function applyMemento(memento: Memento) {
    if (updatingHash) {
        console.log("Ignoring hash update")
        updatingHash = false;
        return;
    }

    try {
        if (assetsPicker.value != memento.assetPack || !currentAssets) {
            assetsPicker.value = memento.assetPack;
            if (memento.assetPack != "zipfile") {
                currentPackSource = new ServedPackSource(packs[assetsPicker.value].url);
                currentAssets = await loadFrom(currentPackSource);
                readAssets();
            } else if (currentAssets == null) {
                zipPicker.style.display = "";
                await new Promise<void>((resolve, reject) => {
                    waitingMemento = resolve;
                });
            }
        }

        for (const group in memento.props) {
            const part = memento.props[group];
            if (!(currentAssets.assets[group]))
                throw new Error("Invalid group " + group);
            if (!(currentAssets.assets[group].items[part]))
                throw new Error("Invalid part " + part + " for group " + group);
            inputs[group].value = part;
        }
        for (const cname in memento.colors) {
            const val = memento.colors[cname];

            if (!(currentAssets.colors[cname]))
                continue; // throw new Error("Invalid color " + cname);
            colorValues[cname] = val;
            colors[cname].picker.setColor(val, true);
            colors[cname].code.innerText = val;
            colors[cname].code.style.backgroundColor = val;
        }
        update(false);
    } catch (e) {
        console.error(e);
    }
}

function readProps() {
    let props: { [group: string]: string } = {};
    for (const type in inputs) {
        const input = inputs[type];
        props[type] = input.value;
    }
    return props;
}

async function update(setHash: boolean) {
    var props = readProps();

    setHashThrottle.trigger();

    var componentsByLayer: { [layer: string]: Component[] } = {};
    for (const layerName in currentAssets.layers) {
        componentsByLayer[layerName] = [];
    }

    for (const colorId in colors) { colors[colorId].inUse = false; }

    for (const groupName in props) {
        const assetType = currentAssets.assets[groupName];
        const prop = props[groupName];
        const arr = (Array.isArray(prop)) ? prop : [prop];

        for (const assetid of arr) {
            const asset = assetType.items[assetid];

            for (const comp of asset.components) {
                componentsByLayer[comp.layer].push(comp);
                if (comp.color != "none")
                    colors[comp.color].inUse = true;
            }
        }
    }
    for (const colorId in colors) { colors[colorId].updateActive(); }

    await compose.render(mainCanvas, currentAssets, currentPackSource, componentsByLayer, colorValues);
}

window.onload = asyncMain;